import { create } from 'zustand';
import { defineSyncedStore, type SyncedStoreDef } from './yjs-provider';
import type { LexiconRule } from '~types/user-data';
import { v4 as uuidv4 } from 'uuid';

/**
 * Replication declaration (aggregated by src/store/registry.ts).
 * merge-defaults + scopedDiff: small user data, low write rate, two keys. The
 * per-entry `r.order || 0` guards below are NOT hydration fallbacks.
 */
export const LEXICON_STORE_DEF: SyncedStoreDef<'rules' | 'settings'> = {
    name: 'lexicon',
    syncedKeys: ['rules', 'settings'],
    hydration: 'merge-defaults',
    scopedDiff: true,
};

export interface LexiconState {
    // === SYNCED STATE (Yjs Map 'lexicon') ===

    /** 
     * Flat map of all rules.
     * Key: UUID (string)
     * Value: LexiconRule
     */
    rules: Record<string, LexiconRule>;

    /**
     * Per-book configuration.
     * Key: bookId (string)
     * Value: Configuration object
     */
    settings: Record<string, {
        bibleLexiconEnabled: 'on' | 'off' | 'default';
    }>;

    // === ACTIONS ===
    addRule: (rule: Omit<LexiconRule, 'id' | 'created' | 'order'>) => void;
    updateRule: (id: string, updates: Partial<LexiconRule>) => void;
    deleteRule: (id: string) => void;
    reorderRules: (updates: { id: string; order: number }[]) => void;
    setBiblePreference: (bookId: string, value: 'on' | 'off' | 'default') => void;
}

export const useLexiconStore = create<LexiconState>()(
    defineSyncedStore(
        LEXICON_STORE_DEF,
        (set) => ({
            rules: {},
            settings: {},

            addRule: (rule) => set((state) => {
                const id = uuidv4();
                // Determine order: max(existing) + 1
                let maxOrder = -1;
                for (const key in state.rules) {
                    if (!Object.prototype.hasOwnProperty.call(state.rules, key)) continue;
                    const r = state.rules[key];
                    if ((r.order || 0) > maxOrder) {
                        maxOrder = r.order || 0;
                    }
                }

                const newRule: LexiconRule = {
                    ...rule,
                    id,
                    created: Date.now(),
                    order: maxOrder + 1
                };

                return {
                    rules: {
                        ...state.rules,
                        [id]: newRule
                    }
                };
            }),

            updateRule: (id, updates) => set((state) => {
                if (!state.rules[id]) return state;
                return {
                    rules: {
                        ...state.rules,
                        [id]: { ...state.rules[id], ...updates }
                    }
                };
            }),

            deleteRule: (id) => set((state) => {
                const { [id]: _removed, ...remainingRules } = state.rules;
                return { rules: remainingRules };
            }),

            reorderRules: (updates) => set((state) => {
                const newRules = { ...state.rules };
                let changed = false;

                updates.forEach(({ id, order }) => {
                    if (newRules[id] && newRules[id].order !== order) {
                        newRules[id] = { ...newRules[id], order };
                        changed = true;
                    }
                });

                if (!changed) return state;
                return { rules: newRules };
            }),

            setBiblePreference: (bookId, value) => set((state) => ({
                settings: {
                    ...state.settings,
                    [bookId]: {
                        ...state.settings[bookId],
                        bibleLexiconEnabled: value
                    }
                }
            }))
        })
    )
);
